import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Star, Clock } from "lucide-react";
import axios from "axios";
import { getBackendUrl } from "../utils/api";

const API = `${getBackendUrl()}/api`;

const FavouriteDishesPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [favouriteDishes, setFavouriteDishes] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [favResponse, restResponse] = await Promise.all([
        axios.get(`${API}/favorites/${user.id}`),
        axios.get(`${API}/restaurants`)
      ]);
      
      setRestaurants(restResponse.data);
      
      // Backend returns {restaurants: [...], dishes: [...]}
      // dishes already contain restaurant_name from backend
      const dishes = favResponse.data.dishes || [];
      setFavouriteDishes(dishes);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (dishId, restaurantId) => {
    try {
      await axios.delete(`${API}/favorites/${user.id}/dish/${dishId}`);
      setFavouriteDishes(prev => prev.filter(d => d.id !== dishId));
    } catch (error) {
      console.error("Error removing favorite:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3FA9F5] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="favourite-dishes-page">
      {/* Top Spacer for mobile */}
      <div className="h-6 bg-white"></div>
      
      {/* Header */}
      <header className="bg-white px-4 py-4 shadow-sm sticky top-0 z-40">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate("/home")}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Favourite Dishes</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {favouriteDishes.length === 0 ? (
          <div className="text-center py-12">
            <Heart size={64} className="text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No favourite dishes yet</h2>
            <p className="text-gray-500 mb-6">Start adding dishes to your favourites!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {favouriteDishes.map((dish) => (
              <div 
                key={dish.id} 
                className="bg-white rounded-xl p-4 shadow-sm flex gap-4"
                data-testid={`fav-dish-${dish.id}`}
              >
                <img 
                  src={dish.image} 
                  alt={dish.name}
                  className="w-20 h-20 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{dish.name}</h3>
                      <p className="text-sm text-gray-500">{dish.restaurant_name}</p>
                      <p className="text-[#3FA9F5] font-semibold mt-1">₹{dish.price}</p>
                    </div>
                    <button 
                      onClick={() => removeFavorite(dish.id, dish.restaurant_id)}
                      className="p-2 hover:bg-gray-100 rounded-full"
                      data-testid={`remove-fav-${dish.id}`}
                    >
                      <Heart size={20} className="text-red-500 fill-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavouriteDishesPage;
